import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { UUIDs } from '../ble/ble';
import DeviceConnection from '../ble/DeviceConnection';
import MapTable from '../ble/MapTable';
import useMapCollection from './useMapCollection';
import { useProtobufDeviceContext } from '../ble/protobufService/ProtobufDeviceContext';
import './CompassEvents.css';

const CompassEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [autoUpload, setAutoUpload] = useState(Cookies.get('autoUploadEvents') === 'true');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [mapSource, setMapSource] = useState('');
  const [numberOfDays, setNumberOfDays] = useState(5);
  const [testGrid, setTestGrid] = useState(false);
  const [includeAdult, setIncludeAdult] = useState(false);
  const [eventsPerDay, setEventsPerDay] = useState(80);
  const [artsPerDay, setArtsPerDay] = useState(10);
  const [artStartTime, setArtStartTime] = useState(2100);
  const [artEndTime, setArtEndTime] = useState(800);
  // Defaults ON: v2 (Options-based) is the source we actually maintain, and it is the only one that
  // honours the email box below. The legacy endpoint stays one un-tick away.
  const [useV2Source, setUseV2Source] = useState(true);
  // CEV2-4: the v2 endpoint re-ranks its results around one person's own favourites/maybes when
  // given ?email=. Kept in a cookie like autoUpload — this is an operator tool and retyping an
  // address on every reload is pure friction.
  const [personalEmail, setPersonalEmail] = useState(Cookies.get('compassEventsEmail') || '');

  // Use the shared protobuf device context
  const {
    deviceConnectionState,
    isDeviceConnected,
    protobufCharacteristics,
    protobufSchemaRoot,
    protobufSchemaProtocol,
    // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
    connectionState,
    isInitialized,
    handleConnect,
    handleDisconnect,
    handleConnectionStateChange,
    sentPoints,
    setSentPoints,
    protobufProtocolService // <-- add this line
  } = useProtobufDeviceContext();

  // Find the events collection characteristic (assuming it uses MapPoint type)
  const eventsCollectionCharacteristic = protobufCharacteristics.find(char => 
    char.isCollection && char.collectionData?.recordProtobufType === 'MapPoint'
  );

  // Use the map collection hook for events
  const eventsCollection = useMapCollection(
    eventsCollectionCharacteristic?.collectionData?.statusUuid,
    eventsCollectionCharacteristic?.collectionData?.recordUuid,
    protobufSchemaRoot,
    protobufSchemaProtocol,
    protobufProtocolService // <-- pass the service explicitly
  );

  // Load collection status after device is connected and collection is initialized
  useEffect(() => {
    let cancelled = false;
    async function loadStatusIfReady() {
      if (isDeviceConnected && eventsCollection.initialized && eventsCollection.loadCollectionStatus) {
        //setLoading(true);
        setError("");
        try {
          await eventsCollection.loadCollectionStatus();
        } catch (err) {
          if (!cancelled) setError("Failed to load collection status: " + (err?.message || err));
        } finally {
          //if (!cancelled) setLoading(false);
        }
      }
    }
    loadStatusIfReady();
    return () => { cancelled = true; };
  }, [isDeviceConnected, eventsCollection.initialized]);

  // Sync local events with collection events when they change
  useEffect(() => {
    if (eventsCollection.localPoints.length > 0) {
      // Allow real-time updates during device downloads (when mapSource is 'compass')
      // Only prevent updates during other loading operations
      if (mapSource === 'compass' && (!loading || eventsCollection.isSyncing)) {
        handleEvents(eventsCollection.localPoints, false, false);
      }
    }
  }, [eventsCollection.localPoints, loading, mapSource, eventsCollection.isSyncing]);

  const handleEvents = (newEvents, forceAdd = false, accumulate = false) => {
    if (!accumulate && !forceAdd) {
      setEvents(newEvents);
    } else {
      setEvents((prevEvents) => {
        const finalEvents = [...prevEvents];

        newEvents.forEach((newEvent) => {
          const match = finalEvents.find((event) =>
            event.id === newEvent.id &&
            event.start === newEvent.start &&
            event.end === newEvent.end
          );
          if (!match || forceAdd) {
            finalEvents.push(newEvent);
          }
        });

        return finalEvents;
      });
    }
  };

  const eventsBaseUrl = useV2Source
    ? 'https://v2-compass-events-fzjaqefbta-uc.a.run.app'
    : 'https://compass-events-fzjaqefbta-uc.a.run.app';

  // One builder feeds both the Fetch button and the KML link, so they cannot drift apart. The
  // email MUST go through encodeURIComponent: a "+salt" address silently arrives as a space
  // otherwise, and the lookup then misses.
  const buildEventsQuery = (leading = '') => {
    const params = [
      `number_of_days=${numberOfDays}`,
      `test_grid=${testGrid}`,
      `include_adult=${includeAdult}`,
      `events_per_day=${eventsPerDay}`,
      `arts_per_day=${artsPerDay}`,
      `art_start_time=${artStartTime}`,
      `art_end_time=${artEndTime}`,
    ];
    if (personalEmail.trim()) params.push(`email=${encodeURIComponent(personalEmail.trim())}`);
    return [...(leading ? [leading] : []), ...params].join('&');
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');
      setProgress({ current: 0, total: 0 });
      const response = await axios.get(`${eventsBaseUrl}?${buildEventsQuery()}`);
      console.log('Response data:', response.data);
      handleEvents(response.data.data, false, false);
      setMapSource(useV2Source ? 'remote-v2' : 'remote');
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Failed to fetch events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchEventsFromDevice = async () => {
    if (!eventsCollectionCharacteristic) {
      setError('Events collection characteristic not found. Please ensure the device supports event collections.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setEvents([]);
      setProgress({ current: 0, total: 0 });
      setSentPoints([]);
      setMapSource('compass');
      
      console.log('Requesting events data from device...');
      
      await eventsCollection.downloadPointsFromCollection((current, total) => {
        setProgress({ current, total });
      });
      
      // The useEffect will handle setting the events and mapSource
      if (eventsCollection.localPoints.length > 0) {
        setProgress({ current: eventsCollection.localPoints.length, total: eventsCollection.localPoints.length });
      }
    } catch (error) {
      console.error('Error requesting events data:', error);
      setError('Failed to request events data from the device. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadEvents = async (uploadEvents) => {
    if (!eventsCollectionCharacteristic) {
      setError('Events collection characteristic not found. Please ensure the device supports event collections.');
      return;
    }

    try {
      setLoading(true);
      setSentPoints([]);
      setError('');
      setProgress({ current: 0, total: uploadEvents.length });
      
      console.log(`📤 Starting upload of ${uploadEvents.length} events to device...`);
      
      await eventsCollection.uploadPointsToCollection(uploadEvents, (current, total) => {
        console.log(`📤 Upload progress: ${current}/${total}`);
        setProgress({ current, total });
        // Add the current event index to sentPoints (0-based index)
        setSentPoints(prev => [...new Set([...prev, current - 1])]);
      }, true);
      
      // Update progress when upload is complete
      setProgress({ current: uploadEvents.length, total: uploadEvents.length });
      // Mark all events as sent when upload is complete
      setSentPoints(uploadEvents.map((_, index) => index));
      
      console.log('✅ Events data uploaded successfully');
    } catch (error) {
      console.error('❌ Error uploading events data:', error);
      setError('Failed to upload events data to the device. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoUploadChange = (event) => {
    setAutoUpload(event.target.checked);
    Cookies.set('autoUploadEvents', event.target.checked, { expires: 365 });
  };

  const handleDeviceConnect = async () => {
    setSentPoints([]);
    
    // Connect to the protobuf service after device connection
    try {
      await handleConnect();
    } catch (error) {
      console.error('Error connecting to protobuf service:', error);
    }
    
    if (autoUpload && events.length > 0 && mapSource !== 'compass') {
      handleUploadEvents(events);
    }
  };

  const handleDeviceDisconnect = () => {
    setSentPoints([]);
    handleDisconnect();
  };

  const handleCleanEvents = () => {
    setEvents([]);
    setError('');
    setMapSource('');
    setProgress({ current: 0, total: 0 });
  };

  useEffect(() => {
    if (autoUpload && events.length === 0) {
      fetchEvents();
    }
  }, [autoUpload]);

  if (!isInitialized) {
    return <div>Initializing Protobuf Protocol Service...</div>;
  }

  return (
    <div className="compass-events">
      <DeviceConnection
        onConnect={handleDeviceConnect}
        onDisconnect={handleDeviceDisconnect}
        onConnectionStateChange={handleConnectionStateChange}
        ServiceUUID={UUIDs.protobufService}
        externalConnectionState={deviceConnectionState}
      />
      
      {isDeviceConnected && (
        <div className="device-buttons">
          <button onClick={handleFetchEventsFromDevice} className="active-button" disabled={loading || eventsCollection.isSyncing}>
            {loading || eventsCollection.isSyncing ? 'Reading...' : `Read events from compass${eventsCollection.collectionStatus ? ` (${eventsCollection.collectionStatus.currentRecords})` : ''}`}
          </button>
          <button
            onClick={() => handleUploadEvents(events)}
            className="active-button"
            disabled={loading || eventsCollection.isSyncing || events.length === 0}
          >
            {loading || eventsCollection.isSyncing ? 'Uploading...' : 'Upload events to compass'}
          </button>
        </div>
      )}

      {/* Device connection status */}
      <div className="connection-status-section">
        {isDeviceConnected ? (
          <div className="connected-status">
            <span className="status-indicator connected">✅ Connected to Device</span>
            {eventsCollection.isSyncing && (
              <span className="sync-indicator">🔄 Syncing with device...</span>
            )}
          </div>
        ) : (
          <div className="disconnected-status">
            <span className="status-indicator disconnected">❌ Not Connected</span>
            <span className="connection-hint">Connect to device to sync event data</span>
          </div>
        )}
      </div>

      <div className="settings-section">
        <p>
          <label>
            <input type="checkbox" checked={autoUpload} onChange={handleAutoUploadChange} />
            Upload events automatically
          </label>
        </p>
        <p>
          <label>
            Number of days: 
            <input 
              type="number" 
              min="1" 
              max="7" 
              value={numberOfDays} 
              onChange={(e) => setNumberOfDays(parseInt(e.target.value) || 2)}
              style={{ marginLeft: '10px', width: '60px' }}
            />
          </label>
        </p>
        <p>
          <label>
            <input 
              type="checkbox" 
              checked={testGrid} 
              onChange={(e) => setTestGrid(e.target.checked)} 
            />
            Test grid
          </label>
        </p>
        <p>
          <label>
            <input 
              type="checkbox" 
              checked={includeAdult} 
              onChange={(e) => setIncludeAdult(e.target.checked)} 
            />
            Include adult events
          </label>
        </p>
        <p>
          <label>
            Events per day: 
            <input 
              type="number" 
              min="1" 
              max="200" 
              value={eventsPerDay} 
              onChange={(e) => setEventsPerDay(parseInt(e.target.value) || 75)}
              style={{ marginLeft: '10px', width: '60px' }}
            />
          </label>
        </p>
        <p>
          <label>
            Arts per day: 
            <input 
              type="number" 
              min="1" 
              max="50" 
              value={artsPerDay} 
              onChange={(e) => setArtsPerDay(parseInt(e.target.value) || 14)}
              style={{ marginLeft: '10px', width: '60px' }}
            />
          </label>
        </p>
        <p>
          <label>
            Art start time (HHMM): 
            <input 
              type="number" 
              min="0" 
              max="2359" 
              value={artStartTime} 
              onChange={(e) => setArtStartTime(parseInt(e.target.value) || 2100)}
              style={{ marginLeft: '10px', width: '80px' }}
              placeholder="2100"
            />
          </label>
        </p>
        <p>
          <label>
            Art end time (HHMM): 
            <input 
              type="number" 
              min="0" 
              max="2359" 
              value={artEndTime} 
              onChange={(e) => setArtEndTime(parseInt(e.target.value) || 800)}
              style={{ marginLeft: '10px', width: '80px' }}
              placeholder="800"
            />
          </label>
        </p>
        <p>
          <button onClick={handleCleanEvents}>
            Clean events
          </button>
        </p>
      </div>

      <div className="events-section">
        <button onClick={fetchEvents} className="active-button" disabled={loading}>
          {loading ? 'Fetching...' : 'Fetch Events from Cloud'}
        </button>
        <a href={`${eventsBaseUrl}?${buildEventsQuery('format=kml')}`} className="active-link">
          Download KML
        </a>
        <p>
          <label>
            <input
              type="checkbox"
              checked={useV2Source}
              onChange={(e) => setUseV2Source(e.target.checked)}
            />
            Use v2 (Options-based) source
          </label>
        </p>
        <p>
          <label>
            Personalize for (email):
            <input
              type="email"
              value={personalEmail}
              onChange={(e) => {
                setPersonalEmail(e.target.value);
                Cookies.set('compassEventsEmail', e.target.value, { expires: 365 });
              }}
              style={{ marginLeft: '10px', width: '260px' }}
              placeholder="you@example.com"
              data-testid="compass-events-email"
            />
          </label>
          <br />
          <small>
            Fill this in and you get <strong>only that person's saved places</strong> — their
            favourites and maybes, nothing else. The per-day numbers above stop topping each day
            back up with things they never picked, so expect a much shorter list. Their saves also
            stay in even when other people rated them badly. Leave blank for the full list. Only
            the v2 source uses this.
          </small>
        </p>
      </div>
      
      {error && <p className="error-message">{error}</p>}

      {/* Events Table */}
      <div className="events-table-container">
        <h3>Events ({events.length})</h3>
        {mapSource === 'compass' && (
          <div className="map-source-indicator">
            <span className="source-badge">📱 Device Source</span>
            {eventsCollection.isConnected && (
              <span className="connection-status">
                {eventsCollection.isSyncing ? '🔄 Syncing...' : '✅ Connected'}
              </span>
            )}
          </div>
        )}
        {mapSource === 'remote' && (
          <div className="map-source-indicator">
            <span className="source-badge">☁️ Cloud Source</span>
          </div>
        )}
        {mapSource === 'remote-v2' && (
          <div className="map-source-indicator">
            <span className="source-badge">☁️ Cloud Source (v2)</span>
          </div>
        )}
        <MapTable
          points={events}
          progress={progress}
          sentPoints={sentPoints}
        />
      </div>
    </div>
  );
};

export default CompassEvents; 